/**
 * PhaseCloseTile.tsx — STEP-OV-PHASE-PROGRESS-TRUTH
 * Shows phase close distance (open steps + exit gate items) and cohort split.
 * Tapping step IDs opens the EntitySheet drill-through via window.__openEntity.
 * UX-031: 44×44px minimum touch targets.
 */
import type { PhaseProgressResponse } from "../api/types.js";

interface Props {
  data: PhaseProgressResponse;
}

interface CohortChipsProps {
  label: string;
  stepIds: string[];
  color: string;
}

function CohortChips({ label, stepIds, color }: CohortChipsProps) {
  if (stepIds.length === 0) return null;

  return (
    <div style={{ marginBottom: 8 }}>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9,
          color: "var(--text-dim)",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 4,
        }}
      >
        {label} ({stepIds.length})
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {stepIds.map((id) => (
          <button
            key={id}
            className="chip"
            onClick={() => window.__openEntity?.("steps", id)}
            style={{
              cursor: "pointer",
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              fontWeight: 600,
              color,
              background: "var(--surface-raised, rgba(255,255,255,0.06))",
              border: `1px solid ${color}`,
              borderRadius: 4,
              padding: "3px 7px",
              minHeight: 28,
              display: "inline-flex",
              alignItems: "center",
            }}
            aria-label={`Open step ${id}`}
          >
            {id}
          </button>
        ))}
      </div>
    </div>
  );
}

export function PhaseCloseTile({ data }: Props) {
  const { close_distance, cohorts } = data;

  return (
    <div data-testid="phase-close-tile" style={{ padding: "8px" }}>
      {/* Close distance summary */}
      <div
        className="card"
        style={{
          padding: "12px 16px",
          marginBottom: 8,
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 8,
        }}
      >
        {[
          { label: "open steps", value: close_distance.open_steps, color: "var(--warn)" },
          { label: "exit gates", value: close_distance.exit_gate_items, color: "var(--text-dim)" },
          { label: "total", value: close_distance.total, color: "var(--text)" },
        ].map((stat) => (
          <div key={stat.label} style={{ textAlign: "center" }}>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 28,
                fontWeight: 700,
                color: stat.color,
                lineHeight: 1,
              }}
            >
              {stat.value}
            </div>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9,
                color: "var(--text-dim)",
                textTransform: "uppercase",
                marginTop: 4,
              }}
            >
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Cohort split */}
      <div className="card" style={{ padding: "12px 16px" }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            color: "var(--text-dim)",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: 10,
          }}
        >
          Cohort split
        </div>
        <CohortChips label="Active Build" stepIds={cohorts.active_build.step_ids} color="var(--accent)" />
        <CohortChips label="Verify Only" stepIds={cohorts.verify_only.step_ids} color="var(--ok)" />
        <CohortChips label="Spec Only" stepIds={cohorts.spec_only.step_ids} color="var(--warn)" />
        {cohorts.phantom_candidate.count > 0 && (
          <CohortChips label="Phantom Candidate" stepIds={cohorts.phantom_candidate.step_ids} color="var(--danger)" />
        )}
        {cohorts.active_build.count === 0 &&
          cohorts.verify_only.count === 0 &&
          cohorts.spec_only.count === 0 &&
          cohorts.phantom_candidate.count === 0 && (
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              color: "var(--ok)",
              textAlign: "center",
              padding: "8px 0",
            }}
          >
            All steps are terminal — phase is ready to close
          </div>
        )}
      </div>
    </div>
  );
}
